import { Collapse, Space, Tag, Typography } from 'antd'

export type RagDebugStep = {
  step: string
  elapsed_ms: number
  step_ms?: number
  data: Record<string, unknown>
}

export type RagDebugPayload = {
  enabled: boolean
  total_ms: number
  answer_mode?: string
  response_ms?: number
  hit_count?: number
  steps: RagDebugStep[]
}

const STEP_LABELS: Record<string, string> = {
  rag_settings: 'RAG 参数',
  input: '用户输入',
  history: '对话历史',
  retrieval_gate: '是否检索知识库',
  agent_mode: 'Agentic 模式',
  agent_loop: 'Agentic 循环',
  agent_step: 'Agentic 步骤',
  agent_finish: 'Agentic 结束',
  web_search: '联网搜索',
  query_rewrite: '检索问句改写',
  deep_think: '深度思考',
  hit_curation: '检索后文档策展',
  grounding_check: '回答 grounding 校验',
  harness_round: '自纠错检索轮次',
  harness_summary: '自纠错汇总',
  entity_scope: '图谱实体匹配',
  doc_scope: '文档范围锁定',
  llm_prepare: '组装大模型请求',
  llm_structured: '结构化回答解析（模型 sources）',
  stream_reply_summary: '流式回答汇总',
  llm: '大模型返回',
  reply: '最终回复',
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  return `${(ms / 60_000).toFixed(1)} min`
}

function stepTimingLabel(s: RagDebugStep): string {
  const stepMs = typeof s.step_ms === 'number' ? s.step_ms : null
  if (stepMs != null && stepMs > 0) {
    return `本步 ${formatMs(stepMs)} · 累计 ${formatMs(s.elapsed_ms)}`
  }
  return `累计 ${formatMs(s.elapsed_ms)}`
}

type ManifestItem = {
  rank?: number
  item_id?: number
  title?: string
  score?: number
}

function ChunkPreviewList({
  chunks,
  maxVisible = 12,
}: {
  chunks: Record<string, unknown>[]
  maxVisible?: number
}) {
  if (!chunks.length) return null
  const shown = chunks.slice(0, maxVisible)
  return (
    <Collapse
      size="small"
      style={{ marginTop: 8 }}
      items={[
        {
          key: 'snippets',
          label: `送入模型的分片（${chunks.length} 条${chunks.length > maxVisible ? `，预览前 ${maxVisible} 条` : ''}）`,
          children: (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
              {shown.map((h, i) => (
                <li
                  key={`${h.item_id}-${h.chunk_index}-${i}`}
                  style={{
                    marginBottom: 10,
                    paddingBottom: 10,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography.Text strong>
                    #{i + 1} · item_id={String(h.item_id ?? '—')}
                    {h.chunk_index != null ? ` · 分片 #${h.chunk_index}` : ''}
                  </Typography.Text>
                  <br />
                  <Typography.Text>{String(h.title || '未命名')}</Typography.Text>
                  {h.score != null ? (
                    <Typography.Text type="secondary"> · score {Number(h.score).toFixed(2)}</Typography.Text>
                  ) : null}
                  {h.es_score != null ? (
                    <Typography.Text type="secondary"> · es {Number(h.es_score).toFixed(2)}</Typography.Text>
                  ) : null}
                  {h.rare_term_bonus != null ? (
                    <Typography.Text type="secondary">
                      {' '}
                      · rare +{Number(h.rare_term_bonus).toFixed(2)}
                    </Typography.Text>
                  ) : null}
                  <Typography.Paragraph
                    type="secondary"
                    style={{ marginBottom: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}
                  >
                    {String(h.snippet_preview || '')}
                  </Typography.Paragraph>
                </li>
              ))}
            </ul>
          ),
        },
      ]}
    />
  )
}

type SourceRefItem = {
  item_id?: number | null
  title?: string | null
  reason?: string
}

function SourcesRefList({
  title,
  items,
  emptyHint,
}: {
  title: string
  items: SourceRefItem[]
  emptyHint?: string
}) {
  return (
    <>
      <Typography.Text strong>{title}</Typography.Text>
      {items.length > 0 ? (
        <ul className="chat-rag-debug-manifest" style={{ margin: '8px 0 12px', paddingLeft: 20 }}>
          {items.map((r, i) => (
            <li key={`${r.item_id}-${i}`}>
              <Typography.Text>
                item_id={r.item_id ?? '—'}
                {r.title ? ` — ${r.title}` : ''}
              </Typography.Text>
              {r.reason ? (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                  {r.reason}
                </Typography.Paragraph>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <Typography.Paragraph type="secondary" style={{ margin: '4px 0 12px' }}>
          {emptyHint || '（空）'}
        </Typography.Paragraph>
      )}
    </>
  )
}

function LlmStructuredBody({ data }: { data: Record<string, unknown> }) {
  const llmRaw = Array.isArray(data.llm_sources_raw)
    ? (data.llm_sources_raw as SourceRefItem[])
    : null
  const resolved = Array.isArray(data.sources_resolved)
    ? (data.sources_resolved as SourceRefItem[])
    : null
  const finalRefs = Array.isArray(data.sources_final)
    ? (data.sources_final as SourceRefItem[])
    : null
  const dropped = Array.isArray(data.sources_dropped_by_filter)
    ? (data.sources_dropped_by_filter as SourceRefItem[])
    : []

  const hasSourcesTrace = llmRaw != null || resolved != null || finalRefs != null

  return (
    <>
      <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
        {data.found != null ? (
          <Tag color={data.found ? 'green' : 'default'}>found={String(data.found)}</Tag>
        ) : null}
        {typeof data.llm_sources_raw_count === 'number' ? (
          <Tag color="blue">模型 sources {data.llm_sources_raw_count} 条</Tag>
        ) : typeof data.refs_count === 'number' ? (
          <Tag>refs {data.refs_count} 条</Tag>
        ) : null}
        {typeof data.sources_final_count === 'number' ? (
          <Tag color="orange">最终展示 {data.sources_final_count} 条</Tag>
        ) : null}
        {data.stream ? <Tag>流式</Tag> : null}
      </Space>
      {typeof data.note === 'string' ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {data.note}
        </Typography.Paragraph>
      ) : null}
      {hasSourcesTrace ? (
        <div className="chat-rag-debug-sources-trace">
          <SourcesRefList
            title="① 模型 JSON 返回的 sources（原始）"
            items={llmRaw || []}
            emptyHint="模型未返回 sources 或 sources 为空数组"
          />
          <SourcesRefList
            title="② 解析后（item_id / 标题与检索命中对齐）"
            items={resolved || []}
            emptyHint="解析后无有效来源（item_id 不在检索命中或未匹配标题）"
          />
          {dropped.length > 0 ? (
            <SourcesRefList title="②→③ 被问句校验过滤掉" items={dropped} />
          ) : null}
          <SourcesRefList
            title="③ 最终展示给用户的参考来源"
            items={finalRefs || []}
            emptyHint="无参考来源（模型未填或被过滤）"
          />
        </div>
      ) : null}
      {typeof data.answer_preview === 'string' && data.answer_preview ? (
        <>
          <Typography.Text strong>answer 预览</Typography.Text>
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
            {data.answer_preview}
          </Typography.Paragraph>
        </>
      ) : null}
      <Collapse
        size="small"
        style={{ marginTop: 8 }}
        items={[{ key: 'raw', label: '技术明细 JSON', children: <JsonBlock data={data} /> }]}
      />
    </>
  )
}

function LlmPrepareBody({ data }: { data: Record<string, unknown> }) {
  if (Array.isArray(data.messages) && !data.document_manifest) {
    return (
      <>
        <Typography.Paragraph type="secondary">
          历史调试记录仍为整段 messages JSON；请重新「重跑」本条以查看结构化清单与分片列表。
        </Typography.Paragraph>
        <JsonBlock data={data} />
      </>
    )
  }

  const manifest = Array.isArray(data.document_manifest)
    ? (data.document_manifest as ManifestItem[])
    : []
  const chunks = Array.isArray(data.context_chunks)
    ? (data.context_chunks as Record<string, unknown>[])
    : []
  const outline = Array.isArray(data.messages_outline)
    ? (data.messages_outline as Record<string, unknown>[])
    : []
  const question = typeof data.user_question === 'string' ? data.user_question : ''

  return (
    <>
      <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
        {data.provider ? <Tag>{String(data.provider)}</Tag> : null}
        {data.model ? <Tag>{String(data.model)}</Tag> : null}
        {typeof data.context_top_k === 'number' ? (
          <Tag color="blue">送入分片上限 {data.context_top_k}</Tag>
        ) : null}
        {typeof data.context_chars === 'number' ? (
          <Tag>上下文字符约 {data.context_chars}</Tag>
        ) : null}
        {data.structured_json ? <Tag>结构化 JSON 回答</Tag> : null}
        {data.deep_think ? <Tag color="purple">深度思考</Tag> : null}
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        实际发给大模型的 user 消息仍包含完整片段正文；此处仅结构化展示清单与分片预览，便于对照 Bad Case。
      </Typography.Paragraph>
      {question ? (
        <>
          <Typography.Text strong>用户问题</Typography.Text>
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{question}</Typography.Paragraph>
        </>
      ) : null}
      {manifest.length > 0 ? (
        <>
          <Typography.Text strong>检索文档清单（{manifest.length} 篇，sources 限选此处 item_id）</Typography.Text>
          <ul className="chat-rag-debug-manifest" style={{ margin: '8px 0 12px', paddingLeft: 20 }}>
            {manifest.map((m) => (
              <li key={`${m.rank}-${m.item_id}`}>
                <Typography.Text>
                  {m.rank}. item_id={m.item_id}{' '}
                  <Typography.Text type="secondary">
                    score={m.score != null ? Number(m.score).toFixed(4) : '—'}
                  </Typography.Text>
                  {' — '}
                  {m.title}
                </Typography.Text>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <ChunkPreviewList chunks={chunks} maxVisible={30} />
      {outline.length > 0 ? (
        <Collapse
          size="small"
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'outline',
              label: '消息角色概要（不含完整 user 正文）',
              children: (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {outline.map((m, i) => (
                    <li key={i} style={{ marginBottom: 8 }}>
                      <Typography.Text strong>{String(m.role)}</Typography.Text>
                      {typeof m.content_chars === 'number' ? (
                        <Typography.Text type="secondary"> · {m.content_chars} 字</Typography.Text>
                      ) : null}
                      {typeof m.note === 'string' ? (
                        <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                          {m.note}
                        </Typography.Paragraph>
                      ) : null}
                      {typeof m.content_preview === 'string' && m.content_preview ? (
                        <Typography.Paragraph
                          type="secondary"
                          style={{ marginBottom: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}
                        >
                          {m.content_preview}
                        </Typography.Paragraph>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
        />
      ) : null}
      <Collapse
        size="small"
        style={{ marginTop: 8 }}
        items={[{ key: 'raw', label: '技术明细 JSON', children: <JsonBlock data={data} /> }]}
      />
    </>
  )
}

function EntityScopeBody({ data }: { data: Record<string, unknown> }) {
  const action = String(data.action || '')
  const actionLabels: Record<string, { color: string; label: string }> = {
    disabled: { color: 'default', label: '未开启' },
    no_graph: { color: 'warning', label: '无图谱数据' },
    no_match: { color: 'default', label: '未命中实体' },
    filter: { color: 'processing', label: '限定检索' },
    boost: { color: 'blue', label: '加权检索' },
  }
  const actionTag = actionLabels[action] || { color: 'default', label: action || '—' }
  const matched = Array.isArray(data.matched_entities)
    ? (data.matched_entities as string[])
    : []
  const itemIds = Array.isArray(data.item_ids) ? (data.item_ids as number[]) : []
  const note = typeof data.note === 'string' ? data.note : ''
  const qPreview = typeof data.question_preview === 'string' ? data.question_preview : ''
  const graphItems =
    typeof data.graph_items_count === 'number' ? data.graph_items_count : null
  const poolSize =
    typeof data.entity_pool_size === 'number' ? data.entity_pool_size : null

  return (
    <>
      <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
        <Tag color={actionTag.color}>{actionTag.label}</Tag>
        {graphItems != null ? <Tag>已审核图谱 {graphItems} 篇</Tag> : null}
        {poolSize != null ? <Tag>实体池 {poolSize} 个</Tag> : null}
        {itemIds.length > 0 ? (
          <Tag color="green">关联文档 {itemIds.length} 篇</Tag>
        ) : null}
        {data.filter_locked ? <Tag color="purple">ES item_id 过滤</Tag> : null}
      </Space>
      {note ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {note}
        </Typography.Paragraph>
      ) : null}
      {qPreview ? (
        <>
          <Typography.Text strong>匹配问句</Typography.Text>
          <Typography.Paragraph style={{ marginBottom: 12, whiteSpace: 'pre-wrap' }}>
            {qPreview}
          </Typography.Paragraph>
        </>
      ) : null}
      {matched.length > 0 ? (
        <>
          <Typography.Text strong>命中实体（{matched.length}）</Typography.Text>
          <ul style={{ margin: '8px 0 12px', paddingLeft: 20 }}>
            {matched.map((name) => (
              <li key={name}>
                <Typography.Text>{name}</Typography.Text>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {itemIds.length > 0 ? (
        <>
          <Typography.Text strong>关联 item_id</Typography.Text>
          <Typography.Paragraph style={{ marginBottom: 12 }}>
            {itemIds.join(', ')}
          </Typography.Paragraph>
        </>
      ) : null}
    </>
  )
}

function ReplyBody({ data }: { data: Record<string, unknown> }) {
  const preview =
    typeof data.answer_preview === 'string' && data.answer_preview
      ? data.answer_preview
      : null
  return (
    <>
      <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
        {data.mode != null ? <Tag>{String(data.mode)}</Tag> : null}
        {data.stream ? <Tag>流式</Tag> : null}
        {data.ok === false ? <Tag color="red">未生成正文</Tag> : null}
        {typeof data.answer_chars === 'number' ? (
          <Tag color="blue">回答 {data.answer_chars} 字</Tag>
        ) : null}
        {typeof data.thinking_chars === 'number' && data.thinking_chars > 0 ? (
          <Tag color="purple">思考 {data.thinking_chars} 字</Tag>
        ) : null}
        {data.deep_think ? <Tag color="purple">深度思考</Tag> : null}
      </Space>
      {preview ? (
        <>
          <Typography.Text strong>answer 预览</Typography.Text>
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
            {preview}
          </Typography.Paragraph>
        </>
      ) : null}
      <Collapse
        size="small"
        style={{ marginTop: 8 }}
        items={[{ key: 'raw', label: '技术明细 JSON', children: <JsonBlock data={data} /> }]}
      />
    </>
  )
}

function HitSummary({ data }: { data: Record<string, unknown> }) {
  const note = typeof data.note === 'string' ? data.note : null
  const hitCount = typeof data.hit_count === 'number' ? data.hit_count : null
  const titles = Array.isArray(data.titles) ? (data.titles as string[]) : []
  const hits = Array.isArray(data.hits) ? (data.hits as Record<string, unknown>[]) : []
  const entityScope =
    data.entity_scope && typeof data.entity_scope === 'object'
      ? (data.entity_scope as Record<string, unknown>)
      : null
  const thinkingMs = typeof data.thinking_ms === 'number' ? data.thinking_ms : null
  const thinkingChars = typeof data.thinking_chars === 'number' ? data.thinking_chars : null
  const hitsScope = data.hits_scope === 'page' ? 'page' : data.hits_scope === 'merged' ? 'merged' : null

  if (!note && hitCount == null && !titles.length && !hits.length) return null

  return (
    <div className="chat-rag-debug-hits" style={{ marginBottom: 12 }}>
      {entityScope ? <EntityScopeBody data={entityScope} /> : null}
      {note ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {note}
        </Typography.Paragraph>
      ) : null}
      {thinkingMs != null || thinkingChars != null ? (
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {thinkingMs != null ? `推理耗时 ${formatMs(thinkingMs)}` : ''}
          {thinkingMs != null && thinkingChars != null ? ' · ' : ''}
          {thinkingChars != null ? `约 ${thinkingChars} 字` : ''}
        </Typography.Text>
      ) : null}
      {hitCount != null ? (
        <Typography.Text>
          合并池 <strong>{hitCount}</strong> 条分片
          {typeof data.page_hit_count === 'number' ? ` · 本页 ${data.page_hit_count}` : ''}
          {typeof data.document_count === 'number' ? ` · ${data.document_count} 篇文档` : ''}
        </Typography.Text>
      ) : null}
      {hitsScope === 'page' ? (
        <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
          下方分片预览为本轮 ES 新拉取页；合并后总池见 merged_hit_count /「组装大模型请求」。
        </Typography.Paragraph>
      ) : null}
      {titles.length > 0 ? (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {titles.map((t) => (
            <li key={t}>
              <Typography.Text>{t}</Typography.Text>
            </li>
          ))}
        </ul>
      ) : null}
      {hits.length > 0 ? (
        <Collapse
          size="small"
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'snippets',
              label: `分片预览（${hits.length} 条）`,
              children: (
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {hits.slice(0, 12).map((h, i) => (
                    <li
                      key={`${h.item_id}-${h.chunk_index}-${i}`}
                      style={{
                        marginBottom: 10,
                        paddingBottom: 10,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography.Text strong>
                        {String(h.title || '未命名')}
                        {h.chunk_index != null ? ` · 分片 #${h.chunk_index}` : ''}
                        {h.score != null ? ` · ${Number(h.score).toFixed(2)}` : ''}
                        {h.es_score != null ? ` · es ${Number(h.es_score).toFixed(2)}` : ''}
                        {h.rare_term_bonus != null ? ` · rare +${Number(h.rare_term_bonus).toFixed(2)}` : ''}
                      </Typography.Text>
                      <Typography.Paragraph
                        type="secondary"
                        style={{ marginBottom: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}
                      >
                        {String(h.snippet_preview || '')}
                      </Typography.Paragraph>
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function JsonBlock({ data }: { data: unknown }) {
  return (
    <pre className="chat-rag-debug-pre">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

function StepBody({ step, data }: { step: string; data: Record<string, unknown> }) {
  if (step === 'llm_prepare') {
    return <LlmPrepareBody data={data} />
  }
  if (step === 'llm_structured') {
    return <LlmStructuredBody data={data} />
  }
  if (step === 'reply' || step === 'stream_reply_summary') {
    return <ReplyBody data={data} />
  }
  if (step === 'entity_scope' || step === 'doc_scope') {
    return (
      <>
        {step === 'entity_scope' ? <EntityScopeBody data={data} /> : null}
        <Collapse
          size="small"
          style={{ marginTop: 8 }}
          items={[{ key: 'raw', label: '技术明细 JSON', children: <JsonBlock data={data} /> }]}
        />
      </>
    )
  }
  const showHits =
    step === 'deep_think' ||
    step === 'harness_round' ||
    (typeof data.hit_count === 'number' && (data.hit_count as number) > 0)
  return (
    <>
      {showHits ? <HitSummary data={data} /> : null}
      <JsonBlock data={data} />
    </>
  )
}

export function ChatRagDebugPanel({
  debug,
  pending = false,
}: {
  debug: RagDebugPayload | null
  pending?: boolean
}) {
  if (pending && (!debug?.steps?.length)) {
    return (
      <div className="chat-rag-debug chat-rag-debug-pending">
        <Typography.Text type="secondary">RAG 调试链路：本轮回答完成后展示各步骤…</Typography.Text>
      </div>
    )
  }
  if (!debug?.steps?.length) return null

  return (
    <div className="chat-rag-debug">
      <div className="chat-rag-debug-head">
        <Typography.Text strong>RAG 调试链路</Typography.Text>
        {debug.enabled ? <Tag color="orange">开发模式</Tag> : null}
        {debug.answer_mode ? <Tag>{debug.answer_mode}</Tag> : null}
        <Typography.Text type="secondary">总耗时 {formatMs(debug.total_ms)}</Typography.Text>
      </div>
      <Collapse
        size="small"
        items={debug.steps.map((s, i) => ({
          key: String(i),
          label: (
            <span>
              {STEP_LABELS[s.step] || s.step}
              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                {stepTimingLabel(s)}
              </Typography.Text>
            </span>
          ),
          children: <StepBody step={s.step} data={s.data} />,
        }))}
      />
      <Typography.Paragraph type="secondary" className="chat-rag-debug-hint">
        自纠错多轮检索：每轮 ES 命中合并后按 rare-term 重排。「组装大模型请求」看重排后的清单与分片；「结构化回答解析」可对照模型 sources 原样、解析后与最终展示三阶段，定位来源是模型填错还是程序过滤导致。
      </Typography.Paragraph>
    </div>
  )
}
